import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { Controller } from 'react-hook-form';

import { CloseButton } from 'pages/TimeClock/modals/base';
import { useTimeStates } from 'hooks/useTimeStates';
import { isIOS, Option } from 'components/Option';
import DurationInput from 'components/DurationInput';

const createOption = (option) => (
  <Option value={option.value} key={option.value}>
    {option.label}
  </Option>
);

const TIME = ['hours', 'minutes'];

export const LunchQuestions = ({ classes, onClose, handleSubmit, clockOutForm }) => {
  const {
    control,
    clearErrors,
    formState: { errors },
    watch,
    reset,
    register,
    unregister,
    setValue,
  } = clockOutForm;
  const { hours, minutes } = useTimeStates({
    hourMaxValue: 13,
    hourStart: 0,
    takeSettingsHours: true,
    overlapsHourMaxValue: true,
  });

  const lunchOptions = useMemo(
    () => [
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ],
    []
  );

  const lunchTime = watch('lunchTime');
  const time = watch(TIME);

  useEffect(() => {
    TIME.forEach((name) => register({ name }));
    return () => unregister(TIME);
  }, [register, unregister]);

  const handleTimeChange = (next) => {
    clearErrors();
    setValue('hours', next.hours);
    setValue('minutes', String(next.minutes).padStart(2, '0'));
  };

  return (
    <>
      <DialogTitle
        disableTypography
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6">Clock Out</Typography>
        <CloseButton style={{ top: 16 }} onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column">
          <label htmlFor="lunchTime">
            <Typography variant="body1" color="textSecondary">
              Did you take a lunch break?
            </Typography>
            <Controller
              control={control}
              name="lunchTime"
              render={({ value }) => (
                <TextField
                  select
                  variant="outlined"
                  fullWidth
                  value={value}
                  onChange={(e) =>
                    reset({ lunchTime: e.target.value, hours: 0, minutes: minutes[0] })
                  }
                  className={classes.lunchTime}
                  inputProps={{
                    'aria-label': 'Did you take a lunch break?',
                  }}
                  SelectProps={{
                    native: isIOS,
                  }}
                >
                  {lunchOptions.map(createOption)}
                </TextField>
              )}
            />
          </label>
          <label>
            <Typography variant="body1" color="textSecondary">
              Lunch Time:
            </Typography>
            <DurationInput
              title="Lunch time"
              value={time}
              onChange={handleTimeChange}
              maxHours={hours.length - 1}
              minuteStep={15}
              disabled={!lunchTime}
              error={Boolean(errors.hours || errors.minutes)}
              className={classes.duration}
            />
            {errors.hours ? <div className={classes.error}>{errors.hours.message}</div> : null}
          </label>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          className={classes.cancelBtn}
          disableTouchRipple
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          className={classes.confirmBtn}
          disableTouchRipple
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};
